import { CardGrid } from "../../../components/CardGrid.tsx";
import { Card } from "../../../components/Card.tsx";
import { FieldDisplay } from "../../../components/FieldDisplay.tsx";
import { PropertyContactDisplay } from "../../../components/PropertyContactDisplay.tsx";
import { OtherContactDisplay } from "../../../components/OtherContactDisplay.tsx";
import {
    ClipboardList,
    DollarSign,
    Landmark,
    MapPin,
    Calendar,
    Home,
    FileText,
    Briefcase,
    HashIcon, AlertTriangle
} from "lucide-react";
import { formatCurrency, formatDate } from "../../../utils/dateUtils.ts";
import { GoogleMap } from "../../../components/google/GoogleMap.tsx";
import React from "react";
import {HtmlContent} from "../../../components/HtmlConent.tsx";
import {OrderDetails} from "../../../api/ApiTypes.ts";
import {getReportTypeDisplayName, isUad36Order} from "./orderDetailsDisplay.ts";
import {Uad36Alert} from "../../../components/Uad36Notice.tsx";

interface OrderDetailsTabProps {
    orderDetails: OrderDetails;
}

export const OrderDetailsTab: React.FC<OrderDetailsTabProps> = ({ orderDetails }) => {
    const reportTypes = [
        orderDetails.reportType1,
        orderDetails.reportType2,
        orderDetails.reportType3,
        orderDetails.reportType4,
    ].filter(Boolean);

    const canSeeContacts = !isNaN(orderDetails.assignedAppraiserSecurityUser) && orderDetails.assignedAppraiserSecurityUser != 0;
    const uad36Order = isUad36Order(orderDetails);

    return (
        <div>

            {uad36Order && <Uad36Alert/>}

            {/* Alerts */}
            {orderDetails.alerts && orderDetails.alerts.length !== 0 && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex justify-between items-center">
                    <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <h3 className="font-medium text-red-800">Alert</h3>
                    </div>
                    <span className="font-medium text-red-800">
                        {orderDetails.alerts.join(', ')}
                    </span>
                </div>
            )}

            {/* Due date banner */}
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4 flex justify-between items-center">
                <h3 className="font-medium text-blue-800">Due Date</h3>
                <span className="font-medium text-blue-800">
                    {formatDate(orderDetails.appraiserDueDate)}
                </span>
            </div>

            <CardGrid>
                {/* Property Information Section */}
                <Card title="Property Information" colSpan={2}>
                    <FieldDisplay
                        label="Property Address"
                        value={
                            <>
                                <p className="text-slate-700">{orderDetails.propertyAddress}</p>
                                {orderDetails.county && (
                                    <p className="text-sm text-slate-500">County: {orderDetails.county}</p>
                                )}
                            </>
                        }
                        icon={<MapPin className="h-5 w-5 text-blue-500" />}
                        className="mb-4"
                    />

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <FieldDisplay
                            label="Property Type"
                            value={orderDetails.propertyType}
                            icon={<Home className="h-5 w-5 text-blue-500" />}
                        />

                        <FieldDisplay
                            label="Intended Use"
                            value={orderDetails.intendedUse}
                            icon={<Briefcase className="h-5 w-5 text-blue-500" />}
                        />

                        <FieldDisplay
                            label="Purchase Price"
                            value={formatCurrency(orderDetails.purchasePrice)}
                            icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails.yearBuilt && (
                            <FieldDisplay
                                label="Year Built"
                                value={orderDetails.yearBuilt}
                                icon={<Calendar className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.livingArea && (
                            <FieldDisplay
                                label="Living Area"
                                value={orderDetails.livingArea}
                                icon={<Home className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.lotArea && (
                            <FieldDisplay
                                label="Lot Area"
                                value={orderDetails.lotArea}
                                icon={<MapPin className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.countyUse && (
                            <FieldDisplay
                                label="County Use"
                                value={orderDetails.countyUse}
                                icon={<Landmark className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        {orderDetails.assessedValue && (
                            <FieldDisplay
                                label="Assessed Value"
                                value={formatCurrency(orderDetails.assessedValue)}
                                icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                            />
                        )}
                    </div>
                </Card>

                {/* Financial & Dates Section */}
                <Card title="Financial Information">
                    <FieldDisplay
                        label="Vendor Fee"
                        value={formatCurrency(orderDetails.appraiserFee)}
                        icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                        className="mb-4"
                    />

                    {Number(orderDetails?.techFee) > 0 && (
                        <FieldDisplay
                            label="Technology Fee"
                            value={formatCurrency(orderDetails.techFee)}
                            icon={<DollarSign className="h-5 w-5 text-blue-500" />}
                        />
                    )}
                </Card>

                {/* Dates & Reference Numbers */}
                <Card title="Dates & References" colSpan={2}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <FieldDisplay
                            label="Inspection Date"
                            value={formatDate(orderDetails.inspectionActualDate)}
                            icon={<Calendar className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails?.estimatedClosingDate && (
                            <FieldDisplay
                                label="Estimated Closing Date"
                                value={formatDate(orderDetails?.estimatedClosingDate)}
                                icon={<Calendar className="h-5 w-5 text-blue-500" />}
                            />
                        )}

                        <FieldDisplay
                            label="Loan Number"
                            value={orderDetails.loanNumber}
                            icon={<HashIcon className="h-5 w-5 text-blue-500" />}
                        />

                        {orderDetails.fhaCaseId && (
                            <FieldDisplay
                                label="FHA Case ID"
                                value={orderDetails.fhaCaseId}
                                icon={<FileText className="h-5 w-5 text-blue-500" />}
                            />
                        )}
                    </div>
                </Card>

                {/* Report Types Section */}
                <Card title="Report Types">
                    <div className="divide-y divide-slate-200">
                        {reportTypes.map((report, index) => (
                            <div key={index} className={index > 0 ? 'pt-2 mt-2' : ''}>
                                <h3 className="font-medium text-slate-900">
                                    {getReportTypeDisplayName(report, orderDetails.deliveryStandard)}
                                </h3>
                            </div>
                        ))}
                    </div>
                </Card>

                {/* Client Info */}
                <Card title="Client Information">
                    <FieldDisplay
                        label="Ordered By"
                        value={
                            <>
                                <h3 className="font-medium text-slate-900">{orderDetails.orderedBy}</h3>
                                <p className="text-slate-700 text-sm mt-1">{orderDetails.orderedByAddress}</p>
                            </>
                        }
                        icon={<Landmark className="h-5 w-5 text-blue-500" />}
                    />
                </Card>

                {/* Contact Information */}
                <Card title="Contact Information" colSpan={2}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {canSeeContacts && orderDetails.propertyContact && (
                            <PropertyContactDisplay contact={orderDetails.propertyContact} />
                        )}

                        {canSeeContacts && orderDetails.otherContacts && orderDetails.otherContacts.length > 0 && (
                            <div className="mb-4">
                                <div className="p-4 bg-white rounded-md border border-slate-200">
                                    {
                                        orderDetails.otherContacts.map((contact) => (
                                            <OtherContactDisplay key={contact.id} contact={contact} />
                                        ))
                                    }
                                </div>
                            </div>
                        )}
                    </div>

                    {!canSeeContacts && (
                        <p className="text-slate-500 italic">Contact information will be visible once you're assigned to this order</p>
                    )}

                    {canSeeContacts && !orderDetails.propertyContact && (!orderDetails.otherContacts || orderDetails.otherContacts.length === 0) && (
                        <p className="text-slate-500 italic">No contacts available</p>
                    )}
                </Card>

                {/* Notes Section */}
                <Card title="Notes & Instructions" colSpan={3}>
                    <div className="flex items-start">
                        <ClipboardList className="h-5 w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="prose prose-sm max-w-none">
                            <HtmlContent
                                content={orderDetails.notesAndSpecialInstructions}
                                className="font-sans text-slate-700"
                                emptyMessage="No notes or instructions available"
                            />
                        </div>
                    </div>
                </Card>

                {/* Property Maps Section */}
                <Card title={orderDetails.propertyAddress} colSpan={3}>
                    <GoogleMap lat={orderDetails.latitude} lng={orderDetails.longitude} />
                </Card>
            </CardGrid>
        </div>
    );
};
